import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, ChevronRight, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useRedEstado } from "@/lib/red";
import { MemberAvatar } from "@/components/red/MemberAvatar";
import { FounderBadge } from "@/components/red/FounderBadge";
import { Section } from "@/components/common/DetailFields";

const MemberRow = ({ m, children, testid = "mired-member" }) => (
  <div data-testid={testid} className="flex items-center gap-3 py-2.5">
    <MemberAvatar m={m} size="w-10 h-10 text-sm" />
    <Link to={`/conecta/u/${m.id}`} className="flex-1 min-w-0">
      <p className="text-sm font-semibold truncate">{m.nombre_publico}</p>
      <p className="text-xs c-muted">{[m.ciudad, m.es_fundador && `Fundador #${m.numero_fundador}`].filter(Boolean).join(" · ") || "CONECTA"}</p>
    </Link>
    {children}
  </div>
);

const Avisos = ({ data }) => (
  <Section title="Avisos" testid="mired-avisos">
    {data.items.length === 0 ? <p className="text-sm c-muted py-2">Sin avisos todavía.</p> : (
      <ul className="divide-y c-line">
        {data.items.slice(0, 10).map((n) => (
          <li key={n.id} data-testid="mired-aviso-item" className="py-2 flex items-start gap-2 text-sm">
            <Bell className={`w-4 h-4 mt-0.5 shrink-0 ${n.leida ? "c-muted" : "c-accent"}`} />
            {n.url ? <Link to={n.url} className={n.leida ? "c-text-2" : "font-semibold"}>{n.texto}</Link> : <span>{n.texto}</span>}
          </li>
        ))}
      </ul>
    )}
  </Section>
);

const Requests = ({ list, onAction }) => list.recibidas.length + list.enviadas.length > 0 && (
  <Section title="Solicitudes pendientes" testid="mired-requests">
    {list.recibidas.map((m) => (
      <MemberRow key={m.connection_id} m={m} testid="mired-request-received">
        <button data-testid="mired-accept-btn" aria-label="Aceptar" onClick={() => onAction(m.connection_id, "aceptar")} className="w-9 h-9 rounded-full c-btn-primary flex items-center justify-center"><Check className="w-4 h-4" /></button>
        <button data-testid="mired-reject-btn" aria-label="Rechazar" onClick={() => onAction(m.connection_id, "rechazar")} className="w-9 h-9 rounded-full border c-line flex items-center justify-center"><X className="w-4 h-4" /></button>
      </MemberRow>
    ))}
    {list.enviadas.map((m) => <MemberRow key={m.connection_id} m={m} testid="mired-request-sent"><span className="text-xs c-muted">Enviada</span></MemberRow>)}
  </Section>
);

export default function MiRedPage() {
  const [estado, reloadEstado] = useRedEstado();
  const [list, setList] = useState(null);
  const [avisos, setAvisos] = useState(null);
  const load = useCallback(() => api.get("/red/conexiones").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    load();
    api.get("/red/avisos").then((r) => { setAvisos(r.data); if (r.data.sin_leer) api.post("/red/avisos/leer").then(reloadEstado); }).catch(() => {});
  }, [load, reloadEstado]);
  const act = async (cid, action) => {
    try { await api.post(`/red/conexiones/${cid}/${action}`); toast.success(action === "aceptar" ? "Conexión aceptada" : "Solicitud rechazada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list || !estado) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  return (
    <div data-testid="mi-red-page" className="space-y-3">
      <div className="c-card p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="font-heading font-bold">{estado.nombre_publico}</p>
          <FounderBadge estado={estado} testid="mired-founder-badge" />
        </div>
        {estado.avalado_por && <p data-testid="mired-avalado" className="text-xs c-text-2">Avalado por <Link to={`/conecta/u/${estado.avalado_por.id}`} className="font-semibold c-accent">{estado.avalado_por.nombre_publico}</Link></p>}
        <p className="text-xs c-muted">Fundadores: {estado.fundadores.asignados} de {estado.fundadores.cupo}</p>
        <Link to={`/conecta/u/${estado.user_id}`} data-testid="mired-my-profile-link" className="text-xs font-semibold c-accent">Ver mi perfil público</Link>
      </div>
      <Link to="/conecta/invitaciones" data-testid="mired-invitations-link" className="c-card p-4 flex items-center gap-3">
        <Send className="w-5 h-5 c-accent" />
        <div className="flex-1"><p className="font-semibold">Mis invitaciones</p><p className="text-xs c-muted">{estado.invitaciones_disponibles == null ? "Ilimitadas (admin)" : `${estado.invitaciones_disponibles} disponibles`}</p></div>
        <ChevronRight className="w-5 h-5 c-muted" />
      </Link>
      {avisos && <Avisos data={avisos} />}
      <Requests list={list} onAction={act} />
      <Section title={`Conexiones (${list.conexiones.length})`} testid="mired-connections">
        {list.conexiones.length === 0 ? <p className="text-sm c-muted py-2">Aún no tienes conexiones. Invita a tus contactos de confianza.</p>
          : <div className="divide-y c-line">{list.conexiones.map((m) => <MemberRow key={m.connection_id} m={m} />)}</div>}
      </Section>
      {list.bloqueadas.length > 0 && (
        <Section title="Bloqueados" testid="mired-blocked"><div className="divide-y c-line">{list.bloqueadas.map((m) => <MemberRow key={m.connection_id} m={m} testid="mired-blocked-member" />)}</div></Section>
      )}
    </div>
  );
}
